import { EditActions } from '@/components';
import { MasterInfoEnum } from '@/constant';
import { useCommonList, useEditableDetail } from '@/hooks';
import { isMobile } from '@/utils/mobile.util';
import React, {
	memo,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';

import { AutoComplete as AutoCompleteAntd, AutoCompleteProps } from 'antd';
import { DefaultOptionType } from 'antd/es/select';
import { cn } from 'utils';

import './style.scss';
import { includes, toLower, uniq } from 'ramda';
import { isNonEmptyArray } from 'ramda-adjunct';

export interface IAutoComplete extends AutoCompleteProps {
	type?: MasterInfoEnum;
	label?: string;
	labelClassName?: string;
	inputClassName?: string;
	containerClassName?: string;
	required?: boolean;
	horizontal?: boolean;
	onChange?: (value: string) => void;
	formatInput?: (value: string, number?: number) => void;
	hasServerError?: boolean;
	errorCode?: string | undefined;
	touched?: boolean;
	name?: string;
	isDetail?: boolean;
	maxLength?: number;
	textEditClassName?: string;
	/** When true, onSelect will pass option.value instead of option.label */
	useValueOnSelect?: boolean;
}

const AutoComplete: React.FC<IAutoComplete> = ({
	type,
	label,
	labelClassName,
	inputClassName = '',
	containerClassName = '',
	required = false,
	horizontal = true,
	maxLength,
	disabled,
	onChange,
	onSelect,
	formatInput,
	hasServerError,
	errorCode,
	touched,
	value,
	isDetail = false,
	textEditClassName,
	useValueOnSelect = false,
	...rest
}) => {
	const { isUpdate, editable, handleLabelClick, onCancel, onSave } =
		useEditableDetail({ fieldName: rest.name as string });

	const [options, setOptions] = useState<DefaultOptionType[] | undefined>([]);
	const [displayText, setDisplayText] = useState<string>('');

	const { selectMasterInfo } = useCommonList(type);

	const innerInputRef = useRef<any>(null);

	useEffect(() => {
		if (type && isNonEmptyArray(selectMasterInfo?.data)) {
			setOptions(uniq(selectMasterInfo?.data || []));
		}
	}, [selectMasterInfo]);

	useEffect(() => {
		if (editable && isUpdate && isDetail) {
			innerInputRef?.current?.focus();
		}
	}, [editable]);

	useEffect(() => {
		if (!useValueOnSelect) {
			setDisplayText(value?.toString() || '');
		} else if (!value) {
			// Reset displayText when value is cleared (e.g., on reset)
			setDisplayText('');
		}
	}, [value, useValueOnSelect]);

	const _options = useMemo(() => {
		if (type) {
			return uniq(selectMasterInfo?.data || []);
		}
		return uniq(rest.options || []);
	}, [type, selectMasterInfo, rest.options]);

	const onSearch = useCallback(
		(searchText: string) => {
			const _searchText = formatInput
				? (formatInput(searchText, maxLength) as unknown as string)
				: searchText;
			// if (isEmpty(searchText)) return setNewOptions([]);
			const _result = _options?.filter((item) =>
				includes(toLower(_searchText), toLower(item?.label as string))
			);

			setOptions(_result);
		},
		[_options]
	);

	const onClear = useCallback(() => {
		onSearch('');
		onChange?.('');
		if (useValueOnSelect) {
			setDisplayText('');
		}
	}, [onSearch, onChange, useValueOnSelect]);

	const _onChange = useCallback(
		(value: string, option: DefaultOptionType | DefaultOptionType[]) => {
			const _value = formatInput
				? (formatInput(value, maxLength) as unknown as string)
				: value;
			if (useValueOnSelect) {
				setDisplayText(_value);
			}
			onChange?.(_value);
		},
		[onChange, formatInput, useValueOnSelect]
	);

	const _onSelect = useCallback(
		(value: string, option: DefaultOptionType | DefaultOptionType[]) => {
			const _option = option as DefaultOptionType;
			const _label = _option?.label as string;
			const _value = _option?.value as string;

			if (useValueOnSelect) {
				setDisplayText(_label);
				onChange?.(_value);
				onSelect?.(_value, option);
			} else {
				onChange?.(_label);
				onSelect?.(_label, option);
			}
		},
		[onChange, onSelect, useValueOnSelect]
	);

	const onFocus = useCallback(() => {
		const currentValue = useValueOnSelect ? displayText : value;
		if (!currentValue) {
			setOptions(_options);
			return;
		}

		const _result = _options?.filter((item) => {
			const itemLabel = item?.label as string;
			return itemLabel && includes(toLower(itemLabel), toLower(currentValue));
		});

		setOptions(_result || []);
	}, [_options, value, useValueOnSelect, displayText]);

	return (
		<div
			className={cn(
				'w-full flex gap-2',
				horizontal ? 'flex-row items-center' : 'flex-col',
				containerClassName
			)}
		>
			{label && (
				<label
					className={cn(
						'text-[13px] font-medium text-font2',
						horizontal && 'min-w-[110px]',
						labelClassName
					)}
				>
					{label}{' '}
					{required && (
						<span
							className="text-red6
					"
						>
							*
						</span>
					)}
				</label>
			)}

			{isDetail && !editable ? (
				<div className="flex w-full">
					<div
						className={cn(
							'break-all box-border w-full text-[13px] md:text-[14px] xl:text-[13px] xl:h-[35px] text-font2 py-[7px] xl:px-[11px] text-left overflow-hidden xl:hover:border xl:hover:border-gray1 cursor-text truncate whitespace-pre',
							disabled &&
								'cursor-not-allowed hover:border-none hover:bg-porcelain',
							textEditClassName
						)}
						onClick={!disabled ? handleLabelClick : undefined}
					>
						{value?.toString() || '-'}
					</div>
				</div>
			) : (
				<div className="flex items-center gap-2 w-full">
					<AutoCompleteAntd
						{...rest}
						ref={innerInputRef}
						value={useValueOnSelect ? displayText : value || ''}
						className={cn(
							'bg-white w-full text-[13px] sm:text-base xl:[&>div.ant-select-selector]:!rounded-none xl:[&>div.ant-select-selector>.ant-select-selection-search>input]:[line-height:30px]',
							inputClassName,
							!horizontal ? 'mt-3' : '',
							disabled && '!bg-disabled !border-none !text-black'
						)}
						status={
							(touched && errorCode) || hasServerError ? 'error' : undefined
						}
						disabled={disabled}
						size="large"
						options={options}
						onChange={_onChange}
						onClear={onClear}
						onSearch={onSearch}
						onSelect={_onSelect}
						onFocus={onFocus}
						allowClear={rest.allowClear}
						defaultOpen={isUpdate && editable && isDetail}
						maxLength={maxLength}
						onBlur={() => {
							isMobile && window.scrollTo(0, 0);
							rest.onBlur;
						}}
					/>
					{isUpdate && isDetail && (
						<EditActions onCancel={onCancel} onSave={onSave} />
					)}
				</div>
			)}
		</div>
	);
};

export default memo(AutoComplete);
